import { useEffect, useState } from 'react'
import { api, type StyleInfo } from '../api'
import { useRefreshOnActivate } from '../useActive'

/**
 * Styles — the chrome, previewed over a real email.
 *
 * A style is PRESENTATION. It carries no sentences: copy comes from the
 * fragments, and a style that held text would be a third copy system in a
 * project that has already had to unpick two.
 *
 * The list leads with `against` rather than with `best for`, which is the
 * opposite of how a gallery usually reads. That is deliberate: every style
 * here looks fine, so "what is this wrong for" is the only field that
 * prevents a mistake. `vintage` reads unhurried, which is right for somebody
 * who just cancelled and actively wrong for a quota warning.
 *
 * Previews render in an iframe. Email markup is table-based and inline-styled
 * so it survives a hostile renderer, and the only honest preview is one a
 * browser has actually laid out — a screenshot or a description would hide
 * exactly the breakage this screen exists to catch.
 */
export function StylesTab({ active = true }: { active?: boolean }) {
  const [data, setData] = useState<{ default: string; styles: StyleInfo[]; note: string } | null>(
    null,
  )
  const [chosen, setChosen] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = async () => {
    try {
      setError(null)
      const res = await api.styles()
      setData(res)
      setChosen((current) => current ?? res.default)
    } catch (err) {
      setError(String((err as Error).message))
    }
  }

  useEffect(() => {
    void load()
  }, [])

  useRefreshOnActivate(active, load)

  if (error) return <div className="error">{error}</div>
  if (!data) return <div className="loading">Loading styles…</div>

  const current = data.styles.find((s) => s.name === chosen) ?? null

  return (
    <>
      <div className="panel">
        <h2>
          Email styles <span className="count">{data.styles.length}</span>
        </h2>
        <div className="panel-body">
          <p className="hint">
            {data.note}
          </p>
        </div>
        <table>
          <thead>
            <tr>
              <th>Style</th>
              <th>Tone</th>
              <th>Not for</th>
              <th className="num">Width</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.styles.map((s) => (
              <tr
                key={s.name}
                className={`clickable${s.name === chosen ? ' selected' : ''}`}
                onClick={() => setChosen(s.name)}
              >
                <td>
                  <span className="mono">{s.name}</span>
                  {s.name === data.default && <span className="badge ok"> default</span>}
                  <div className="hint caption">
                    {s.title}
                  </div>
                </td>
                <td style={{ maxWidth: 220 }}>{s.tone ?? '—'}</td>
                <td style={{ maxWidth: 320 }}>{s.against ?? '—'}</td>
                <td className="num mono">{typeof s.width === 'number' ? s.width : '—'}</td>
                <td>
                  {/* `images` is prose, not a flag — "none — the wordmark is
                      set in type." Testing it for equality with 'none' badged
                      every style as loading images, including the seven that
                      load nothing at all. A remote image matters because most
                      clients block it by default, so the badge has to be
                      right or it is worse than absent. */}
                  {s.problems.length > 0 ? (
                    <span className="badge bad">unusable</span>
                  ) : (s.images ?? '').trim().toLowerCase().startsWith('none') ? (
                    <span className="badge catchall">no images</span>
                  ) : (
                    <span className="badge parked" title={s.images ?? undefined}>
                      remote image
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {current && <StyleDetail style={current} />}
    </>
  )
}

function StyleDetail({ style, draftId }: { style: StyleInfo; draftId?: number }) {
  const [text, setText] = useState<string | null>(null)
  const [html, setHtml] = useState<string | null>(null)
  const [showText, setShowText] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setText(null)
    setShowText(false)
    setHtml(null)
    setError(null)
    void (async () => {
      try {
        setHtml(await api.stylePreview(style.name, draftId))
      } catch (err) {
        setError(String((err as Error).message))
      }
    })()
  }, [style.name, draftId])

  const loadText = async () => {
    setShowText(true)
    if (text === null) {
      try {
        setText((await api.styleText(style.name)).text)
      } catch (err) {
        setText(`could not load: ${(err as Error).message}`)
      }
    }
  }

  return (
    <div className="panel">
      <h2>
        {style.title} <span className="count mono">{style.name}</span>
      </h2>
      <div className="panel-body">
        {style.problems.length > 0 && (
          <div className="error">
            {style.problems.join('; ')}
          </div>
        )}
        <dl className="facts">
          <dt>Reach for it</dt>
          <dd>{style.best_for ?? '—'}</dd>
          <dt>Not for</dt>
          <dd>{style.against ?? '—'}</dd>
          <dt>Images</dt>
          <dd>{style.images ?? '—'}</dd>
          <dt>Dark mode</dt>
          <dd>{String(style.dark_mode ?? '—')}</dd>
        </dl>

        <div className="row">
          <button onClick={() => setShowText(false)} disabled={!showText}>
            HTML
          </button>
          <button onClick={() => void loadText()} disabled={showText}>
            Plain text
          </button>
          <span className="hint">
            Both parts go out together. An HTML-only email is an accessibility failure and a
            deliverability one — filters read a missing text part as a signal.
          </span>
        </div>
      </div>

      {showText ? (
        <pre className="notes panel-note">
          {text ?? 'Loading…'}
        </pre>
      ) : error ? (
        <div className="error panel-note">{error}</div>
      ) : html === null ? (
        <div className="loading">Rendering…</div>
      ) : (
        <iframe
          title={`${style.name} preview`}
          srcDoc={html}
          // Sandboxed: this is markup destined for a mail client, and the
          // preview should not be able to script the operator's session even
          // though we wrote it ourselves. Nothing in an email needs script.
          sandbox=""
          style={{
            width: '100%',
            height: 760,
            border: 0,
            borderTop: '1px solid var(--line)',
            background: '#fff',
          }}
        />
      )}
    </div>
  )
}
